import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageWrapper from '../components/PageWrapper';
import CreateAgencyModal from '../components/CreateAgencyModal';
import { getAgencies, deleteAgency } from '../services/agencyService';
import {
  BuildingOfficeIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  PhoneIcon,
  EnvelopeIcon,
  TrashIcon,
  PencilIcon,
  MapPinIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const AgencyList = () => {
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadAgencies();
  }, []);

  const loadAgencies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAgencies();
      setAgencies(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      console.error('Error loading agencies:', err);
      setError(err.message || 'Failed to load agencies');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAgency = async (agencyId, agencyName) => {
    if (window.confirm(`Are you sure you want to delete "${agencyName}"? This action cannot be undone.`)) {
      try {
        await deleteAgency(agencyId);
        loadAgencies();
      } catch (err) {
        console.error('Error deleting agency:', err);
        alert(err.message || 'Failed to delete agency');
      }
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    loadAgencies();
  };

  const filteredAgencies = agencies.filter(agency =>
    agency.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agency.contact_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agency.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <PageWrapper>
        <div className="flex justify-center items-center py-20">
          <div className="text-center">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-emerald-200 rounded-full animate-pulse"></div>
              <div className="absolute top-0 left-0 w-16 h-16 border-4 border-emerald-500 rounded-full border-t-transparent animate-spin"></div>
            </div>
            <p className="mt-4 text-gray-600 font-medium">Loading agencies...</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper noPadding>
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="text-white">
              <h1 className="text-3xl font-bold">Agencies</h1>
              <p className="mt-2 text-emerald-100">Manage your property management agencies and teams</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 md:mt-0 inline-flex items-center px-5 py-2.5 bg-white text-emerald-600 rounded-xl font-medium hover:bg-emerald-50 transition-all shadow-lg shadow-emerald-700/20"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create Agency
            </button>
          </div>

          {/* Search Bar */}
          <div className="mt-8 max-w-xl">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search agencies by name, email, or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/95 backdrop-blur border-0 rounded-xl shadow-lg focus:ring-2 focus:ring-white/50 placeholder-gray-400"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 -mt-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Agencies</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{agencies.length}</p>
              </div>
              <div className="bg-emerald-100 p-3 rounded-xl">
                <BuildingOfficeIcon className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Agents</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {agencies.reduce((sum, a) => sum + (a.agents?.length || a.agent_count || 0), 0)}
                </p>
              </div>
              <div className="bg-blue-100 p-3 rounded-xl">
                <UserGroupIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Properties</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {agencies.reduce((sum, a) => sum + (a.properties?.length || a.property_count || 0), 0)}
                </p>
              </div>
              <div className="bg-purple-100 p-3 rounded-xl">
                <BuildingOfficeIcon className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
              <span className="text-red-600 text-xs">!</span>
            </div>
            <div className="flex-1">
              <p className="text-red-700">{error}</p>
              <button
                onClick={loadAgencies}
                className="mt-2 text-red-600 hover:text-red-800 text-sm font-medium underline"
              >
                Try again
              </button>
            </div>
          </div>
        )}

        {/* Agency Cards */}
        {filteredAgencies.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="bg-gray-100 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto">
              <BuildingOfficeIcon className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="mt-6 text-xl font-semibold text-gray-900">
              {agencies.length === 0 ? 'No agencies yet' : 'No matching agencies'}
            </h3>
            <p className="mt-2 text-gray-500 max-w-sm mx-auto">
              {agencies.length === 0
                ? 'Get started by creating your first property management agency.'
                : 'Try adjusting your search terms to find what you\'re looking for.'}
            </p>
            {agencies.length === 0 && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-6 inline-flex items-center px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors shadow-lg shadow-emerald-500/25"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Your First Agency
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAgencies.map((agency) => (
              <div
                key={agency.id}
                className="group bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-xl hover:border-emerald-200 transition-all duration-300 overflow-hidden"
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 px-6 py-5 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="bg-gradient-to-br from-emerald-400 to-emerald-600 p-2.5 rounded-xl shadow-lg shadow-emerald-500/20">
                        <BuildingOfficeIcon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors">
                          {agency.name}
                        </h3>
                        {agency.currency && (
                          <span className="text-xs text-gray-500">{agency.currency}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  <div className="space-y-3">
                    {agency.address && (
                      <div className="flex items-start text-gray-600">
                        <MapPinIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm line-clamp-2">{agency.address}</span>
                      </div>
                    )}
                    {agency.contact_email && (
                      <div className="flex items-center text-gray-600">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                        <span className="text-sm truncate">{agency.contact_email}</span>
                      </div>
                    )}
                    {agency.contact_phone && (
                      <div className="flex items-center text-gray-600">
                        <PhoneIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">{agency.contact_phone}</span>
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="mt-5 pt-5 border-t border-gray-100 grid grid-cols-2 gap-4">
                    <div className="text-center bg-gray-50 rounded-lg py-2">
                      <div className="text-xl font-bold text-gray-900">
                        {agency.properties?.length || agency.property_count || 0}
                      </div>
                      <div className="text-xs text-gray-500">Properties</div>
                    </div>
                    <div className="text-center bg-gray-50 rounded-lg py-2">
                      <div className="text-xl font-bold text-gray-900">
                        {agency.agents?.length || agency.agent_count || 0}
                      </div>
                      <div className="text-xs text-gray-500">Agents</div>
                    </div>
                  </div>
                </div>

                {/* Card Footer */}
                <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between">
                  <Link
                    to={`/agencies/${agency.id}`}
                    className="inline-flex items-center text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
                  >
                    Manage Agents
                    <ArrowRightIcon className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <div className="flex items-center space-x-1">
                    <Link
                      to={`/agencies/${agency.id}/edit`}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Edit Agency"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Link>
                    <button
                      onClick={() => handleDeleteAgency(agency.id, agency.name)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete Agency"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Results Count */}
        {filteredAgencies.length > 0 && (
          <div className="mt-8 text-center text-sm text-gray-500">
            Showing {filteredAgencies.length} of {agencies.length} agencies
          </div>
        )}
      </div>

      {/* Create Agency Modal */}
      <CreateAgencyModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />
    </PageWrapper>
  );
};

export default AgencyList;
